import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader } from "../../components/dash/ui";
import { api, formatApiError } from "../../lib/api";
import { ROLE_LABELS } from "../../lib/labels";
import { AlertDetail } from "../../components/security/AlertDetail";
import { RiskBadge, StatusBadge, wib } from "../../components/security/badges";

const RISKS = ["KRITIS", "TINGGI", "SEDANG", "RENDAH"];
const POLL_MS = 30000;

const Summary = ({ s }) => (
  <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6" data-testid="security-summary">
    {RISKS.map((r) => (
      <div key={r} className={`rounded-xl border p-4 ${["KRITIS", "TINGGI"].includes(r) && s.openByRisk[r] ? "border-red-400 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`summary-open-${r}`}>
        <p className="text-xs font-semibold text-slate-500">Belum ditangani · {r}</p><p className="text-2xl font-bold text-slate-900">{s.openByRisk[r]}</p>
      </div>
    ))}
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="summary-sessions"><p className="text-xs font-semibold text-slate-500">Sesi aktif</p><p className="text-2xl font-bold">{s.activeSessions}</p></div>
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="summary-failed"><p className="text-xs font-semibold text-slate-500">Login gagal 24 jam</p><p className="text-2xl font-bold">{s.failedLogins24h}</p></div>
    <p className="col-span-2 lg:col-span-6 text-xs text-slate-500" data-testid="summary-backup">Akun nonaktif: {s.inactiveAccounts} · Backup terakhir: {wib(s.lastBackupAt)} · {s.backupNote}</p>
  </div>
);

const Filters = ({ f, setF }) => {
  const sel = (k, opts) => (
    <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`filter-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="security-filters">
      {sel("risk", [["", "Semua risiko"], ...RISKS.map((r) => [r, r])])}
      {sel("status", [["OPEN", "Belum ditangani"], ["", "Semua status"], ["BARU", "Baru"], ["DITINJAU", "Ditinjau"], ["DITANGANI", "Ditangani"]])}
      <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cari akun, email, organisasi, IP" className="h-9 w-64" data-testid="filter-q" />
      <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="h-9 w-40" data-testid="filter-from" />
      <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="h-9 w-40" data-testid="filter-to" />
    </div>
  );
};

const AlertRow = ({ a, onOpen }) => (
  <button onClick={() => onOpen(a.id)} className={`w-full text-left rounded-lg border p-3 hover:bg-slate-50 transition-colors ${["KRITIS", "TINGGI"].includes(a.risk) && a.status !== "DITANGANI" ? "border-red-300 border-l-4 border-l-red-600" : "border-slate-200"}`} data-testid={`alert-row-${a.id}`}>
    <div className="flex flex-wrap items-center gap-2"><RiskBadge risk={a.risk} /><StatusBadge status={a.status} /><span className="font-semibold text-slate-900 text-sm">{a.title}</span>{a.count > 1 && <span className="text-xs text-slate-500">{a.count}x</span>}</div>
    <p className="mt-1 text-xs text-slate-600">{a.userName || "Tanpa akun"} ({a.userEmail || a.ip}) · {ROLE_LABELS[a.userRole] || a.userRole || "-"}{a.orgName ? ` · ${a.orgName}` : ""} · terakhir {wib(a.lastSeenAt)}</p>
  </button>
);

const Table = ({ rows, cols, testid, onUser }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{cols.map(([, l]) => <th key={l} className="p-2">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => r.userId && onUser(r.userId)}>
          {cols.map(([k, l, fn]) => <td key={l} className="p-2 whitespace-nowrap">{fn ? fn(r[k]) : r[k] || "-"}</td>)}
        </tr>))}
      </tbody>
    </table>
  </div>
);

export default function AdminSecurity() {
  const [s, setS] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [logins, setLogins] = useState([]);
  const [f, setF] = useState({ risk: "", status: "OPEN", q: "", from: "", to: "" });
  const [open, setOpen] = useState({ alertId: null, userId: null });
  const load = useCallback(async () => {
    const params = { risk: f.risk || undefined, status: f.status || undefined, q: f.q || undefined,
      from: f.from ? `${f.from}T00:00:00+07:00` : undefined, to: f.to ? `${f.to}T23:59:59+07:00` : undefined };
    try {
      const [a, b, c, d] = await Promise.all([api.get("/admin/security/summary"), api.get("/admin/security/alerts", { params }),
        api.get("/admin/security/sessions"), api.get("/admin/security/logins")]);
      setS(a.data); setAlerts(b.data); setSessions(c.data); setLogins(d.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [f]);
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="admin-security-page">
      <PageHeader title="Pusat Keamanan" subtitle="Pantau aktivitas mencurigakan dan ambil tindakan per akun. Peringatan adalah indikator risiko, bukan bukti otomatis pelanggaran. Diperbarui otomatis tiap 30 detik." />
      {s && <Summary s={s} />}
      <Tabs defaultValue="alerts">
        <TabsList className="mb-4 flex-wrap h-auto justify-start">
          <TabsTrigger value="alerts" data-testid="tab-alerts"><ShieldAlert className="h-4 w-4 mr-1" /> Peringatan ({s?.openTotal ?? 0})</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sesi Aktif</TabsTrigger>
          <TabsTrigger value="logins" data-testid="tab-logins">Riwayat Login & Tindakan</TabsTrigger>
        </TabsList>
        <TabsContent value="alerts">
          <Filters f={f} setF={setF} />
          <div className="space-y-2" data-testid="alerts-list">
            {alerts.map((a) => <AlertRow key={a.id} a={a} onOpen={(id) => setOpen({ alertId: id, userId: null })} />)}
            {!alerts.length && <p className="text-sm text-slate-500 py-6" data-testid="alerts-empty">Tidak ada peringatan sesuai filter.</p>}
          </div>
        </TabsContent>
        <TabsContent value="sessions">
          <Table testid="sessions-table" rows={sessions} onUser={(u) => setOpen({ alertId: null, userId: u })}
            cols={[["userName", "Akun"], ["userEmail", "Email"], ["userRole", "Peran", (v) => ROLE_LABELS[v] || v], ["deviceLabel", "Perangkat"], ["ip", "IP"], ["lastSeenAt", "Aktif terakhir", wib]]} />
        </TabsContent>
        <TabsContent value="logins">
          <Table testid="logins-table" rows={logins} onUser={(u) => setOpen({ alertId: null, userId: u })}
            cols={[["createdAt", "Waktu", wib], ["action", "Kejadian"], ["userName", "Akun"], ["email", "Email"], ["device", "Perangkat"], ["ip", "IP"]]} />
        </TabsContent>
      </Tabs>
      <AlertDetail alertId={open.alertId} userId={open.userId} onClose={() => setOpen({ alertId: null, userId: null })} onChanged={load} />
    </div>
  );
}
